import React, { useState, useEffect } from 'react';
import { GitCompare, X } from 'lucide-react';
import {
  ACOUSTIC_CAMERAS,
  getCameraById,
} from '../data/acousticCameras';

/**
 * Sticky "Compare with…" bar pinned to the bottom-right of every
 * individual acoustic-camera product page.  Lets a visitor jump straight
 * from anywhere on the page into the comparison modal without scrolling
 * back to the hero.
 *
 * Behaviour:
 *   • Hidden by default; appears once the visitor scrolls past ~500 px
 *     (i.e. below the hero) so it never overlaps the hero CTAs.
 *   • Dismissable — an X button hides the bar for the current session.
 *   • Selecting another model from the dropdown fires `onCompareRequest`
 *     with that camera id; the product page opens its existing
 *     CompareCamerasModal pre-filled with the selection.
 *
 * Props:
 *   primaryId          string — id of the camera the product page is for
 *   onCompareRequest   (otherId) => void — called when user picks a model
 */
const StickyCompareBar = ({ primaryId, onCompareRequest }) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const primary = getCameraById(primaryId);
  const others = ACOUSTIC_CAMERAS.filter((c) => c.id !== primaryId);
  if (!primary || dismissed || !visible) return null;

  const handleChange = (e) => {
    const otherId = e.target.value;
    if (otherId && otherId !== 'placeholder') {
      onCompareRequest(otherId);
      // Reset the select back to placeholder so it can be reused later.
      e.target.value = 'placeholder';
    }
  };

  return (
    <div
      className="fixed z-40 bottom-24 right-4 sm:bottom-28 sm:right-6 max-w-[calc(100vw-2rem)] pointer-events-auto"
      data-testid="sticky-compare-bar"
      role="complementary"
      aria-label="Compare acoustic camera models"
    >
      <div className="bg-[#1e2d3b] text-white rounded-full shadow-2xl border border-white/10 pl-4 pr-1 py-1.5 flex items-center gap-2 sm:gap-3">
        <GitCompare className="w-4 h-4 text-blue-300 flex-shrink-0" />
        <label htmlFor="sticky-compare-select" className="text-xs sm:text-sm whitespace-nowrap">
          Compare <span className="font-semibold">{primary.name}</span> with:
        </label>
        <select
          id="sticky-compare-select"
          defaultValue="placeholder"
          onChange={handleChange}
          data-testid="sticky-compare-select"
          className="bg-white/10 hover:bg-white/15 focus:bg-white/15 text-white text-xs sm:text-sm font-medium rounded-full px-3 py-1.5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer max-w-[45vw] sm:max-w-none truncate"
        >
          <option value="placeholder" disabled className="text-slate-900">
            Select model…
          </option>
          {others.map((c) => (
            <option key={c.id} value={c.id} className="text-slate-900">
              FOTRIC {c.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss compare bar"
          data-testid="sticky-compare-dismiss"
          className="flex-shrink-0 w-8 h-8 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default StickyCompareBar;
